/**
 * GoodGuardian — Agent Handoff Utilities
 *
 * Shared config and classifier for detecting when a user is on the wrong
 * agent screen and needs to be redirected to a better-matched specialist.
 */
import { getToken } from '@/utils/auth'

// ── Agent metadata & route mapping ───────────────────────────────────────────
export const AGENT_INFO = {
  'chat':             { label: 'Legal Chat',        icon: '💬', route: '/chat',          color: 'bg-blue-100   text-blue-700'   },
  'case-analyst':     { label: 'Case Analyst',      icon: '🔍', route: '/case-analyst',  color: 'bg-purple-100 text-purple-700' },
  'document-drafter': { label: 'Document Drafter',  icon: '📄', route: '/documents',     color: 'bg-teal-100   text-teal-700'   },
  'cps-advisor':      { label: 'CPS Advisor',       icon: '🛡️', route: '/cps',           color: 'bg-orange-100 text-orange-700' },
  'client-coach':     { label: 'Court Coach',       icon: '⚖️', route: '/coach',         color: 'bg-indigo-100 text-indigo-700' },
  'researcher':       { label: 'Legal Researcher',  icon: '📚', route: '/ask',           color: 'bg-green-100  text-green-700'  },
}

// Prefill key each screen reads from React Router location.state
export const PREFILL_KEY = 'prefill'

/**
 * Lightweight intent classifier — calls /api/orchestrate and returns only
 * the routing decision without waiting for a full agent response.
 *
 * We reuse the same orchestrator endpoint; the response includes `routed_to`
 * and `confidence` before we even look at the reply content.
 *
 * Returns null on any error so callers can silently skip the redirect.
 */
export async function classifyIntent(message, user = {}) {
  try {
    const token = getToken()
    if (!token) return null

    const res = await fetch('/api/orchestrate', {
      method: 'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        message,
        history: [],
        user,
        state: user.state,
        // Signal to orchestrator that this is a classify-only call so it can
        // skip the expensive agent sub-call in the future if desired
        classify_only: true,
      }),
    })

    if (!res.ok) return null
    const data = await res.json()
    return {
      agent:      data.routed_to || 'chat',
      confidence: data.confidence ?? 0,
      reason:     data.routing_reason || '',
    }
  } catch {
    return null
  }
}
